import React, { useState, useEffect } from 'react';
import { 
  Typography, Box, TextField, Grid, Paper, Button, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, 
  DialogActions, Chip, Avatar, List, ListItem, ListItemText, ListItemIcon,
  Divider, Alert, Tabs, Tab, Card, CardContent, Pagination, Autocomplete, 
  IconButton, Accordion, AccordionSummary, AccordionDetails, LinearProgress, Stack,
  Container, InputAdornment, Tooltip, alpha, CircularProgress
} from '@mui/material';

import { 
  PersonAdd as PersonAddIcon,
  Search as SearchIcon, 
  School as SchoolIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarMonth,
  Score,
  CheckCircle,
  MenuBook,
  Schedule,
  Analytics,
  Assessment,
  Cancel,
  TrendingUp,
  SignalCellular1Bar,
  SignalCellular2Bar,
  SignalCellular3Bar,
  Person,
  Badge,
  AccountCircle,
  Email,
  AdminPanelSettings,
  AccessTime,
  Pending,
  Assignment,
  SupervisorAccount,
  People,
  LibraryBooks,
  CalendarToday,
  CardMembership,
  History,
  Visibility,
  Download,
  Close as CloseIcon
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import api, { 
  getAllUsersWithProgress, 
  getAllCoursesWithProgress, 
  enrollUserToCourse, 
  getAllDepartments, 
  getAllDeadlines,
  getAllCertificates,
  downloadCertificatesByCourse,
  downloadCertificatesByUser,
  getUserTestHistory,
  exportHistoryToPDF,
  exportHistoryToExcel,
  getGMPHistory
} from '../services/api';
import SupervisorCalendar from '../componets/Calendar/SupervisorCalendar';
import TestHistory from '../componets/TestHistory';
import CourseTestAnalytics from '../componets/CourseTestAnalytics';
import EnrollDialog from '../componets/EnrollDialog';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9'
};

// Colores por estado
const STATUS_COLORS = {
  completed: { color: COLORS.success, icon: CheckCircle, label: 'Completado' },
  in_progress: { color: COLORS.primary, icon: Schedule, label: 'En Progreso' },
  not_started: { color: COLORS.light, icon: Pending, label: 'Pendiente' }
};

const departmentOptions = [
  'Producción',
  'Mantenimiento',
  'Calidad',
  'Proceso',
  'Administración',
  'Logística',
  'General'
];

// Colores por departamento
const DEPT_COLORS = {
  'Producción': COLORS.success,
  'Mantenimiento': COLORS.warning,
  'Calidad': COLORS.primary,
  'Proceso': COLORS.purple,
  'Administración': COLORS.orange,
  'Logística': COLORS.info,
  'General': '#636e72'
};

const getDeptColor = (dept) => DEPT_COLORS[dept] || '#636e72';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SupervisorDashboard = () => {
  // Estados
  const [tabValue, setTabValue] = useState(0);
  const [employeeData, setEmployeeData] = useState([]);
  const [coursesData, setCoursesData] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [openEnrollDialog, setOpenEnrollDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [deadlineDate, setDeadlineDate] = useState(null);
  const [departments, setDepartments] = useState(['Todos']);
  const [usersInDepartment, setUsersInDepartment] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [completedCoursesPage, setCompletedCoursesPage] = useState(1);
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [certificatePage, setCertificatePage] = useState(1);
  const [testTabValue, setTestTabValue] = useState(0);
  const [isAnnual, setIsAnnual] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [selectedHistoryCourse, setSelectedHistoryCourse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;
  const [filteredHistoryData, setFilteredHistoryData] = useState([]);
  const certificatesPerPage = 9;

  // Effects
  useEffect(() => {
    fetchData();
    fetchDepartments();
    fetchDeadlines();
    fetchCertificates();
  }, []);

  useEffect(() => {
    if (tabValue === 4) fetchHistoryData();
  }, [tabValue]);

  // Funciones principales
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersResponse, coursesResponse] = await Promise.all([
        getAllUsersWithProgress(),
        getAllCoursesWithProgress()
      ]);

      setEmployeeData(usersResponse);
      setFilteredEmployees(usersResponse);
      setCoursesData(coursesResponse);
      setFilteredCourses(coursesResponse);
      setUsersInDepartment([
        { id: 'all', username: 'Todos los usuarios' },
        ...usersResponse
      ]);
    } catch (error) {
      console.error('Error al obtener datos:', error);
      setError('Hubo un problema al cargar los datos. Por favor, intente de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async () => {
    try {
      setLoadingHistory(true);
      const response = await getGMPHistory(selectedHistoryCourse?.id);
      const sortedData = response.sort((a, b) => 
        new Date(b.change_date) - new Date(a.change_date)
      );
      setHistoryData(sortedData);
      setFilteredHistoryData(sortedData);
    } catch (error) {
      console.error('Error fetching GMP history:', error);
      setError('Error al cargar el historial GMP');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleExportHistory = async (format) => {
    try {
      let blob;
      if (format === 'pdf') {
        blob = await exportHistoryToPDF();
        saveAs(blob, 'historial-gmp.pdf');
      } else {
        blob = await exportHistoryToExcel();
        saveAs(blob, 'historial-gmp.xlsx');
      }
    } catch (error) {
      console.error('Error exporting history:', error);
      setError('Error al exportar el historial');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await getAllDepartments();
      setDepartments(['Todos', ...response]);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchDeadlines = async () => {
    try {
      const response = await getAllDeadlines();
      setDeadlines(response);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await getAllCertificates();
      setCertificates(response);
      setFilteredCertificates(response);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  // Manejadores de eventos
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchTerm('');
    if (newValue === 0) {
      setFilteredEmployees(employeeData);
    } else if (newValue === 1) {
      setFilteredCourses(coursesData);
    } else if (newValue === 3) {
      setFilteredCertificates(certificates);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    if (tabValue === 0) {
      const filtered = employeeData.filter(employee => 
        employee.username?.toLowerCase().includes(term) ||
        employee.email?.toLowerCase().includes(term) ||
        employee.nombre?.toLowerCase().includes(term)
      );
      setFilteredEmployees(filtered);
    } else if (tabValue === 1) {
      const filtered = coursesData.filter(course => 
        course.title?.toLowerCase().includes(term)
      );
      setFilteredCourses(filtered);
    } else if (tabValue === 3) {
      const filtered = certificates.filter(cert => 
        cert.course_title?.toLowerCase().includes(term) ||
        cert.username?.toLowerCase().includes(term)
      );
      setFilteredCertificates(filtered);
    }
  };

  const handleShowDetails = (item) => {
    setSelectedDetails(item);
    setShowDetailsDialog(true);
  };

  const handleOpenDialog = (item) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenEnrollDialog = (course) => {
    setSelectedCourse(course);
    setOpenEnrollDialog(true);
  };

  const handleCloseEnrollDialog = () => {
    setOpenEnrollDialog(false);
    setSelectedDepartment('');
    setSelectedUser('');
    setDeadlineDate(null);
    setIsAnnual(false);
  };

  const handleDepartmentChange = (event) => {
    const dept = event.target.value;
    setSelectedDepartment(dept);
    setSelectedUser('');
  
    if (dept === 'Todos') {
      setUsersInDepartment([
        { id: 'all', username: 'Todos los usuarios' },
        ...employeeData
      ]);
    } else {
      const filteredUsers = employeeData.filter(user => user.department === dept);
      setUsersInDepartment([
        { id: 'all', username: `Todos los usuarios de ${dept}` },
        ...filteredUsers
      ]);
    }
  };

  const handleEnrollUser = async () => {
    try {
      if (!selectedCourse || !selectedUser || !deadlineDate) {
        console.error('Faltan datos requeridos');
        return;
      }
  
      if (selectedUser === 'all') {
        const usersToEnroll = selectedDepartment === 'Todos' 
          ? employeeData 
          : employeeData.filter(user => user.department === selectedDepartment);
        
        await enrollUserToCourse({
          courseId: selectedCourse.id,
          userIds: usersToEnroll.map(user => user.id),
          deadline: deadlineDate,
          isAnnual
        });
      } else {
        await enrollUserToCourse({
          courseId: selectedCourse.id,
          userId: selectedUser,
          deadline: deadlineDate,
          isAnnual
        });
      }
      fetchData();
      fetchDeadlines();
      handleCloseEnrollDialog();
    } catch (error) {
      console.error('Error al inscribir usuario(s):', error);
      setError('Error al inscribir usuario(s). Por favor, intente de nuevo.');
    }
  };

  const handleDownloadCertificatesByCourse = async () => {
    if (selectedCourse) {
      try {
        await downloadCertificatesByCourse(selectedCourse.id);
      } catch (error) {
        console.error('Error downloading certificates by course:', error);
        setError('Error al descargar certificados.');
      }
    }
  };

  const handleDownloadCertificatesByUser = async () => {
    if (selectedUser) {
      try {
        await downloadCertificatesByUser(selectedUser.id);
      } catch (error) {
        console.error('Error downloading certificates by user:', error);
        setError('Error al descargar certificados.');
      }
    }
  };

  // Tabs config
  const tabs = [
    { label: 'Usuarios', icon: <People /> },
    { label: 'Cursos', icon: <LibraryBooks /> },
    { label: 'Calendario', icon: <CalendarToday /> },
    { label: 'Certificados', icon: <CardMembership /> },
    { label: 'Control de Cambios', icon: <History /> }
  ];

  // Course Card Component
  const CourseCard = ({ course }) => (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        border: '2px solid #e0e0e0',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px ${alpha(COLORS.gradient1, 0.15)}`,
          borderColor: alpha(COLORS.gradient1, 0.3)
        }
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          height: '80px', 
          mb: 2,
          background: `linear-gradient(135deg, ${alpha(COLORS.gradient1, 0.1)} 0%, ${alpha(COLORS.gradient2, 0.1)} 100%)`,
          p: 2,
          margin: -2,
          marginBottom: 2,
          borderRadius: '10px 10px 0 0',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              color: COLORS.dark
            }}
          >
            {course.name}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            backgroundColor: alpha(COLORS.primary, 0.08),
            p: 1,
            borderRadius: 2
          }}>
            <CalendarMonth sx={{ color: COLORS.primary, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.dark }}>
              {course.completion_date ? 
                new Date(course.completion_date).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                }) : 'N/A'}
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            backgroundColor: alpha(COLORS.success, 0.08),
            p: 1,
            borderRadius: 2
          }}>
            <Score sx={{ color: COLORS.success, fontSize: 20 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.success }}>
                {course.test_score !== null && course.test_score !== undefined ? `${course.test_score}%` : 'N/A'}
              </Typography>
              {course.test_score !== null && course.test_score !== undefined && (
                <LinearProgress 
                  variant="determinate" 
                  value={course.test_score} 
                  sx={{ 
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: alpha(COLORS.success, 0.2),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: COLORS.success,
                      borderRadius: 3
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}>
          <Chip 
            icon={<CheckCircle />}
            label={course.certificate_issued ? 'Certificado' : 'Sin cert.'} 
            size="small"
            sx={{ 
              bgcolor: course.certificate_issued ? alpha(COLORS.success, 0.15) : alpha('#636e72', 0.1),
              color: course.certificate_issued ? COLORS.success : '#636e72',
              fontWeight: 600,
              border: 'none',
              '& .MuiChip-icon': { color: course.certificate_issued ? COLORS.success : '#636e72' }
            }}
          />
          {course.certificate_issued && 
            <SchoolIcon sx={{ color: COLORS.primary, fontSize: 24 }} />
          }
        </Box>
      </CardContent>
    </Card>
  );

  // Loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)'
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: `0 8px 32px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <SupervisorAccount sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: 600, mb: 2 }}>
          Cargando panel de supervisor...
        </Typography>
        <Box sx={{ width: 200 }}>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: alpha(COLORS.gradient1, 0.2),
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                borderRadius: 2
              }
            }} 
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fffe 0%, #f0f7ff 100%)' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SupervisorAccount sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: -0.5 }}>
                  Panel de Supervisor
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                  Gestión de usuarios, cursos y certificados
                </Typography>
              </Box>
            </Box>
            
            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ textAlign: 'center', px: 3, py: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>{employeeData.length}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Usuarios</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 3, py: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>{coursesData.length}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Cursos</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 3, py: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>{certificates.length}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Certificados</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            overflow: 'hidden'
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: '2px solid #e0e0e0', background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: `linear-gradient(90deg, ${COLORS.gradient1}, ${COLORS.gradient2})`
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          backgroundColor: tabValue === index ? alpha(COLORS.primary, 0.15) : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {React.cloneElement(tab.icon, {
                          sx: { fontSize: 20, color: tabValue === index ? COLORS.primary : '#9e9e9e' }
                        })}
                      </Box>
                      <Typography sx={{ fontWeight: tabValue === index ? 700 : 500, fontSize: '0.9rem', color: tabValue === index ? COLORS.primary : '#666' }}>
                        {tab.label}
                      </Typography>
                    </Box>
                  }
                  sx={{ textTransform: 'none', minHeight: 64, px: 3 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content */}
          
          {/* Tab 0 y 1: Usuarios y Cursos */}
          {(tabValue === 0 || tabValue === 1) && (
            <Box sx={{ p: 3 }}>
              {/* Search Bar */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  border: '2px solid #e0e0e0',
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <TextField
                  placeholder={tabValue === 0 ? "Buscar usuarios..." : "Buscar cursos..."}
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: COLORS.gradient1 }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    flexGrow: 1,
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: COLORS.gradient1 },
                      '&.Mui-focused fieldset': { borderColor: COLORS.gradient1 }
                    }
                  }}
                />
                <Chip
                  label={`${tabValue === 0 ? filteredEmployees.length : filteredCourses.length} ${tabValue === 0 ? 'usuarios' : 'cursos'}`}
                  sx={{
                    bgcolor: alpha(COLORS.primary, 0.1),
                    color: COLORS.primary,
                    fontWeight: 600
                  }}
                />
              </Paper>

              {/* Table */}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: '2px solid #e0e0e0' }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      {tabValue === 0 ? (
                        <>
                          {['Usuario', 'Nombre', 'Completados', 'En Progreso', 'Inscritos', 'Última Actividad', 'Acciones'].map((header) => (
                            <TableCell
                              key={header}
                              sx={{
                                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                py: 2
                              }}
                            >
                              {header}
                            </TableCell>
                          ))}
                        </>
                      ) : (
                        <>
                          {['Curso', 'Completados', 'En Progreso', 'Inscritos', 'Acciones', 'Inscribir'].map((header) => (
                            <TableCell
                              key={header}
                              sx={{
                                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                py: 2
                              }}
                            >
                              {header}
                            </TableCell>
                          ))}
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tabValue === 0 ? (
                      filteredEmployees.map((employee, index) => (
                        <TableRow
                          key={employee.id}
                          sx={{
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fffe',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(COLORS.gradient1, 0.08)
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: alpha(COLORS.primary, 0.15),
                                  color: COLORS.primary,
                                  fontWeight: 700,
                                  fontSize: '0.9rem'
                                }}
                              >
                                {employee.username?.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontWeight: 600 }}>@{employee.username}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>{employee.nombre} {employee.apellido}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<CheckCircle />}
                              label={employee.completed_courses} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(COLORS.success, 0.15), 
                                color: COLORS.success,
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: COLORS.success }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<Schedule />}
                              label={employee.in_progress_courses} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(COLORS.primary, 0.15), 
                                color: COLORS.primary,
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: COLORS.primary }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<Pending />}
                              label={employee.not_started_courses} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha('#636e72', 0.15), 
                                color: '#636e72',
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: '#636e72' }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#636e72' }}>
                              {employee.last_activity ? new Date(employee.last_activity).toLocaleDateString('es-ES') : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ver detalles">
                              <IconButton
                                onClick={() => handleOpenDialog(employee)}
                                sx={{ color: COLORS.primary, '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) } }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      filteredCourses.map((course, index) => (
                        <TableRow
                          key={course.id}
                          sx={{
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fffe',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(COLORS.gradient1, 0.08)
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 2,
                                  bgcolor: alpha(COLORS.purple, 0.15),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <MenuBook sx={{ color: COLORS.purple, fontSize: 20 }} />
                              </Box>
                              <Typography sx={{ fontWeight: 600, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {course.title}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<CheckCircle />}
                              label={course.completed_count} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(COLORS.success, 0.15), 
                                color: COLORS.success,
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: COLORS.success }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<Schedule />}
                              label={course.in_progress_count} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha(COLORS.primary, 0.15), 
                                color: COLORS.primary,
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: COLORS.primary }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={<Pending />}
                              label={course.enrolled_count} 
                              size="small"
                              sx={{ 
                                bgcolor: alpha('#636e72', 0.15), 
                                color: '#636e72',
                                fontWeight: 700,
                                border: 'none',
                                '& .MuiChip-icon': { color: '#636e72' }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ver detalles">
                              <IconButton
                                onClick={() => handleOpenDialog(course)}
                                sx={{ color: COLORS.primary, '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) } }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleOpenEnrollDialog(course)}
                              sx={{
                                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                  boxShadow: `0 4px 12px ${alpha(COLORS.gradient1, 0.4)}`
                                }
                              }}
                            >
                              Inscribir
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: Calendario */}
          {tabValue === 2 && (
            <Box sx={{ p: 3 }}>
              <SupervisorCalendar deadlines={deadlines} />
            </Box>
          )}

          {/* Tab 3: Certificados */}
          {tabValue === 3 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Buscar por Curso */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '2px solid #e0e0e0',
                      height: '100%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: alpha(COLORS.purple, 0.15),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <MenuBook sx={{ color: COLORS.purple }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                        Buscar por Curso
                      </Typography>
                    </Box>
                    <Autocomplete
                      options={coursesData}
                      getOptionLabel={(option) => option.title || ''}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Seleccionar Curso" 
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: COLORS.purple },
                              '&.Mui-focused fieldset': { borderColor: COLORS.purple }
                            } 
                          }}
                        />
                      )}
                      onChange={(event, newValue) => {
                        setSelectedCourse(newValue);
                        if (newValue) {
                          const filtered = certificates.filter(cert => cert.course_id === newValue.id);
                          setFilteredCertificates(filtered);
                        } else {
                          setFilteredCertificates(certificates);
                        }
                        setCertificatePage(1);
                      }}
                    />
                    <Button 
                      variant="contained" 
                      startIcon={<Download />}
                      onClick={handleDownloadCertificatesByCourse}
                      disabled={!selectedCourse}
                      sx={{ 
                        mt: 2,
                        background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none'
                      }}
                    >
                      Descargar Certificados
                    </Button>
                  </Paper>
                </Grid>

                {/* Buscar por Usuario */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '2px solid #e0e0e0',
                      height: '100%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: alpha(COLORS.primary, 0.15),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Person sx={{ color: COLORS.primary }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                        Buscar por Usuario
                      </Typography>
                    </Box>
                    <Autocomplete
                      options={employeeData}
                      getOptionLabel={(option) => option.username || ''}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Buscar Usuario" 
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: COLORS.primary },
                              '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                            } 
                          }}
                        />
                      )}
                      onChange={(event, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue) {
                          const filtered = certificates.filter(cert => cert.user_id === newValue.id);
                          setFilteredCertificates(filtered);
                        } else {
                          setFilteredCertificates(certificates);
                        }
                        setCertificatePage(1);
                      }}
                    />
                    {selectedUser && (
                      <Typography sx={{ mt: 1, color: '#636e72' }}>
                        {selectedUser.nombre} {selectedUser.apellido}
                      </Typography>
                    )}
                    <Button 
                      variant="contained" 
                      startIcon={<Download />}
                      onClick={handleDownloadCertificatesByUser}
                      disabled={!selectedUser}
                      sx={{ 
                        mt: 2,
                        background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none'
                      }}
                    >
                      Descargar Certificados
                    </Button>
                  </Paper>
                </Grid>

                {/* Tabla de Certificados */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ borderRadius: 3, border: '2px solid #e0e0e0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CardMembership sx={{ color: COLORS.success }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                        Todos los Certificados
                      </Typography>
                      <Chip 
                        label={`${filteredCertificates.length} certificados`}
                        size="small"
                        sx={{ ml: 'auto', bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600 }}
                      />
                    </Box>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {['Usuario', 'Curso', 'Fecha', 'Acciones'].map((header) => (
                              <TableCell
                                key={header}
                                sx={{
                                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                  color: 'white',
                                  fontWeight: 700,
                                  py: 2
                                }}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredCertificates
                            .slice((certificatePage - 1) * certificatesPerPage, certificatePage * certificatesPerPage)
                            .map((cert, index) => (
                              <TableRow
                                key={cert.id}
                                sx={{
                                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fffe',
                                  '&:hover': { backgroundColor: alpha(COLORS.gradient1, 0.08) }
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontSize: '0.8rem' }}>
                                      {cert.username?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography sx={{ fontWeight: 600 }}>{cert.username}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontWeight: 500 }}>{cert.course_title}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: '#636e72' }}>
                                    {new Date(cert.completion_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Button 
                                    variant="contained"
                                    size="small"
                                    startIcon={<Visibility />}
                                    onClick={() => window.open(
                                      `${process.env.REACT_APP_API_URL.replace('/api', '')}${cert.pdf_path.startsWith('/') ? '' : '/'}${cert.pdf_path}`, 
                                      '_blank'
                                    )}
                                    sx={{
                                      background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                      fontWeight: 600,
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      boxShadow: 'none'
                                    }}
                                  >
                                    Ver
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <Pagination 
                        count={Math.ceil(filteredCertificates.length / certificatesPerPage)}
                        page={certificatePage}
                        onChange={(event, value) => setCertificatePage(value)}
                        sx={{
                          '& .MuiPaginationItem-root': {
                            fontWeight: 600,
                            '&.Mui-selected': {
                              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                              color: 'white'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 4: Control de Cambios */}
          {tabValue === 4 && (
            <Box sx={{ p: 3 }}>
              {loadingHistory && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={50} sx={{ color: '#7c3aed' }} />
                  <Typography sx={{ mt: 2, fontWeight: 600, color: 'text.secondary' }}>Cargando historial de cambios...</Typography>
                </Box>
              )}
              {!loadingHistory && (
              <Paper elevation={0} sx={{ borderRadius: 3, border: '2px solid #e0e0e0' }}>
                <Box sx={{ p: 3, borderBottom: '2px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <History sx={{ color: COLORS.purple }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                        Historial de Modificaciones GMP
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => handleExportHistory('pdf')}
                        sx={{ 
                          borderColor: COLORS.error, 
                          color: COLORS.error,
                          fontWeight: 600,
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover': { borderColor: COLORS.error, bgcolor: alpha(COLORS.error, 0.08) }
                        }}
                      >
                        PDF
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={() => handleExportHistory('excel')}
                        sx={{ 
                          borderColor: COLORS.success, 
                          color: COLORS.success,
                          fontWeight: 600,
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover': { borderColor: COLORS.success, bgcolor: alpha(COLORS.success, 0.08) }
                        }}
                      >
                        Excel
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Autocomplete
                      options={coursesData || []}
                      getOptionLabel={(option) => option.title || ''}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Filtrar por curso" 
                          size="small"
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: COLORS.purple },
                              '&.Mui-focused fieldset': { borderColor: COLORS.purple }
                            } 
                          }}
                        />
                      )}
                      onChange={(event, newValue) => {
                        setSelectedHistoryCourse(newValue);
                        setCurrentPage(1);
                        if (!newValue) {
                          setFilteredHistoryData(historyData);
                        } else {
                          const filtered = historyData.filter(item => 
                            item.course_title.toLowerCase() === newValue.title.toLowerCase()
                          );
                          setFilteredHistoryData(filtered);
                        }
                      }}
                      value={selectedHistoryCourse}
                      sx={{ maxWidth: 400 }}
                    />
                  </Box>
                </Box>

                {(!historyData || historyData.length === 0) ? (
                  <Box sx={{ p: 6, textAlign: 'center' }}>
                    <History sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#636e72', mb: 1 }}>
                      No hay modificaciones registradas
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                      Los cambios en los cursos se mostrarán aquí
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {['Fecha', 'Curso', 'Versión', 'Usuario', 'Razón', 'Detalles'].map((header) => (
                              <TableCell
                                key={header}
                                sx={{
                                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                  color: 'white',
                                  fontWeight: 700,
                                  py: 2
                                }}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredHistoryData
                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                            .map((item, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fffe',
                                  '&:hover': { backgroundColor: alpha(COLORS.gradient1, 0.08) }
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {new Date(item.change_date).toLocaleString('es-ES')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontWeight: 600 }}>{item.course_title}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${item.previous_version} → ${item.version}`}
                                    size="small"
                                    sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {item.modified_by_firstname} {item.modified_by_lastname}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.reason}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Ver detalles">
                                    <IconButton
                                      onClick={() => handleShowDetails(item)}
                                      sx={{ color: COLORS.info, '&:hover': { bgcolor: alpha(COLORS.info, 0.1) } }}
                                    >
                                      <InfoIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <Pagination
                        count={Math.ceil(filteredHistoryData.length / itemsPerPage)}
                        page={currentPage}
                        onChange={(event, value) => setCurrentPage(value)}
                        sx={{
                          '& .MuiPaginationItem-root': {
                            fontWeight: 600,
                            '&.Mui-selected': {
                              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                              color: 'white'
                            }
                          }
                        }}
                      />
                    </Box>
                  </>
                )}
              </Paper>
              )}
            </Box>
          )}
        </Paper>

        {/* Dialog: Detalles de Cambios GMP */}
        {showDetailsDialog && selectedDetails && (
          <Dialog 
            open={showDetailsDialog} 
            onClose={() => setShowDetailsDialog(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <History />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Cambios en versión {selectedDetails?.version}
                </Typography>
              </Box>
              <Chip 
                label={`Anterior: ${selectedDetails?.previous_version}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedDetails?.detalleCambios && (
                <Box sx={{ pt: 1 }}>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    <strong>Razón del cambio:</strong> {selectedDetails.reason}
                  </Alert>

                  {/* Cambios en Título */}
                  {selectedDetails.detalleCambios.titulo && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
                        Cambios en el Título
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: alpha(COLORS.primary, 0.05), borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#636e72' }}>Anterior:</Typography>
                            <Typography sx={{ fontWeight: 500 }}>{selectedDetails.detalleCambios.titulo.anterior}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#636e72' }}>Nuevo:</Typography>
                            <Typography sx={{ fontWeight: 600, color: COLORS.success }}>{selectedDetails.detalleCambios.titulo.nuevo}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {/* Cambios en Descripción */}
                  {selectedDetails.detalleCambios.descripcion && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
                        Cambios en la Descripción
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: alpha(COLORS.primary, 0.05), borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#636e72' }}>Anterior:</Typography>
                            <Typography variant="body2">{selectedDetails.detalleCambios.descripcion.anterior}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#636e72' }}>Nueva:</Typography>
                            <Typography variant="body2" sx={{ color: COLORS.success }}>{selectedDetails.detalleCambios.descripcion.nuevo}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {/* Cambios en Categoría */}
                  {selectedDetails.detalleCambios.categoria && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
                        Cambios en la Categoría
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: alpha(COLORS.primary, 0.05), borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#636e72' }}>Anterior:</Typography>
                            <Chip label={selectedDetails.detalleCambios.categoria.anterior} size="small" sx={{ mt: 0.5 }} />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#636e72' }}>Nueva:</Typography>
                            <Chip label={selectedDetails.detalleCambios.categoria.nuevo} size="small" color="success" sx={{ mt: 0.5 }} />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )}

                  {/* Cambios en Archivos */}
                  {['pdfs', 'imagenes', 'videos'].map((tipo) => (
                    selectedDetails.detalleCambios[tipo] && (
                      <Box key={tipo} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
                          Cambios en {selectedDetails.detalleCambios[tipo].tipo}
                        </Typography>
                        
                        {selectedDetails.detalleCambios[tipo].archivosEliminados?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: COLORS.error, fontWeight: 600, mb: 1 }}>
                              Archivos Eliminados
                            </Typography>
                            <List dense sx={{ bgcolor: alpha(COLORS.error, 0.05), borderRadius: 2 }}>
                              {selectedDetails.detalleCambios[tipo].archivosEliminados.map((archivo, idx) => (
                                <ListItem key={idx}>
                                  <ListItemIcon>
                                    {tipo === 'pdfs' ? <PictureAsPdfIcon sx={{ color: COLORS.error }} /> :
                                     tipo === 'imagenes' ? <ImageIcon sx={{ color: COLORS.error }} /> :
                                     <VideoLibraryIcon sx={{ color: COLORS.error }} />}
                                  </ListItemIcon>
                                  <ListItemText primary={archivo.split('/').pop()} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {selectedDetails.detalleCambios[tipo].archivosNuevos?.length > 0 && (
                          <Box>
                            <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600, mb: 1 }}>
                              Archivos Nuevos
                            </Typography>
                            <List dense sx={{ bgcolor: alpha(COLORS.success, 0.05), borderRadius: 2 }}>
                              {selectedDetails.detalleCambios[tipo].archivosNuevos.map((archivo, idx) => (
                                <ListItem key={idx}>
                                  <ListItemIcon>
                                    {tipo === 'pdfs' ? <PictureAsPdfIcon sx={{ color: COLORS.success }} /> :
                                     tipo === 'imagenes' ? <ImageIcon sx={{ color: COLORS.success }} /> :
                                     <VideoLibraryIcon sx={{ color: COLORS.success }} />}
                                  </ListItemIcon>
                                  <ListItemText primary={archivo.split('/').pop()} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Box>
                    )
                  ))}

                  {/* Cambios en Lecciones */}
                  {selectedDetails?.old_lessons && selectedDetails?.current_lessons && 
                    JSON.stringify(selectedDetails.old_lessons) !== JSON.stringify(selectedDetails.current_lessons) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
                        Cambios en Lecciones
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: COLORS.error, fontWeight: 600, mb: 1 }}>
                          Versión Anterior ({selectedDetails.old_lessons?.length || 0} lecciones)
                        </Typography>
                        {(selectedDetails.old_lessons || []).map((leccion, idx) => (
                          <Accordion key={idx} sx={{ mb: 1, bgcolor: alpha(COLORS.error, 0.05), borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography sx={{ fontWeight: 600 }}>{leccion.title}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <div dangerouslySetInnerHTML={{ __html: leccion.content }} />
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600, mb: 1 }}>
                          Versión Nueva ({selectedDetails.current_lessons?.length || 0} lecciones)
                        </Typography>
                        {(selectedDetails.current_lessons || []).map((leccion, idx) => (
                          <Accordion key={idx} sx={{ mb: 1, bgcolor: alpha(COLORS.success, 0.05), borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography sx={{ fontWeight: 600 }}>{leccion.title}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <div dangerouslySetInnerHTML={{ __html: leccion.content }} />
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Cambios en Preguntas */}
                  {selectedDetails.detalleCambios.preguntas && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 1 }}>
                        Cambios en Preguntas del Test
                      </Typography>
                      
                      {selectedDetails.detalleCambios.preguntas.preguntasEliminadas?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: COLORS.error, fontWeight: 600, mb: 1 }}>
                            Preguntas Eliminadas
                          </Typography>
                          {selectedDetails.detalleCambios.preguntas.preguntasEliminadas.map((pregunta, idx) => (
                            <Accordion key={idx} sx={{ mb: 1, bgcolor: alpha(COLORS.error, 0.05), borderRadius: 2 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                  <Typography sx={{ fontWeight: 600 }}>{pregunta.question}</Typography>
                                  <Chip label={pregunta.difficulty} size="small" sx={{ mt: 0.5 }} />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <List dense>
                                  {pregunta.options?.map((option, optIdx) => (
                                    <ListItem key={optIdx}>
                                      <ListItemIcon>
                                        {option === pregunta.correct_answer ? 
                                          <CheckCircleOutlineIcon sx={{ color: COLORS.success }} /> : 
                                          <RadioButtonUncheckedIcon />
                                        }
                                      </ListItemIcon>
                                      <ListItemText primary={option} />
                                    </ListItem>
                                  ))}
                                </List>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      )}

                      {selectedDetails.detalleCambios.preguntas.preguntasNuevas?.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600, mb: 1 }}>
                            Preguntas Nuevas
                          </Typography>
                          {selectedDetails.detalleCambios.preguntas.preguntasNuevas.map((pregunta, idx) => (
                            <Accordion key={idx} sx={{ mb: 1, bgcolor: alpha(COLORS.success, 0.05), borderRadius: 2 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                  <Typography sx={{ fontWeight: 600 }}>{pregunta.question}</Typography>
                                  <Chip label={pregunta.difficulty} size="small" color="success" sx={{ mt: 0.5 }} />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <List dense>
                                  {pregunta.options?.map((option, optIdx) => (
                                    <ListItem key={optIdx}>
                                      <ListItemIcon>
                                        {option === pregunta.correctAnswer ? 
                                          <CheckCircleOutlineIcon sx={{ color: COLORS.success }} /> : 
                                          <RadioButtonUncheckedIcon />
                                        }
                                      </ListItemIcon>
                                      <ListItemText primary={option} />
                                    </ListItem>
                                  ))}
                                </List>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button 
                onClick={() => setShowDetailsDialog(false)}
                sx={{ 
                  fontWeight: 600,
                  borderRadius: 2
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Dialog: Detalles Usuario/Curso */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {tabValue === 0 ? <Person /> : <MenuBook />}
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedItem?.username || selectedItem?.title}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedItem && (
              <Grid container spacing={3}>
                {tabValue === 0 ? (
                  <>
                    {/* Info Usuario */}
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                          <Person sx={{ color: COLORS.primary }} />
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>Información del Usuario</Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <Stack spacing={2}>
                              <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: 1, p: 2, bgcolor: alpha(COLORS.primary, 0.08), borderRadius: 2 }}>
                                  <Typography variant="caption" sx={{ color: '#636e72' }}>Nombre Completo</Typography>
                                  <Typography sx={{ fontWeight: 600 }}>{selectedItem.nombre} {selectedItem.apellido}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, p: 2, bgcolor: alpha(COLORS.success, 0.08), borderRadius: 2 }}>
                                  <Typography variant="caption" sx={{ color: '#636e72' }}>Usuario</Typography>
                                  <Typography sx={{ fontWeight: 600 }}>@{selectedItem.username}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, p: 2, bgcolor: alpha(COLORS.warning, 0.08), borderRadius: 2 }}>
                                  <Typography variant="caption" sx={{ color: '#636e72' }}>Rol</Typography>
                                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{selectedItem.role}</Typography>
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: 1, p: 2, bgcolor: alpha(COLORS.error, 0.08), borderRadius: 2 }}>
                                  <Typography variant="caption" sx={{ color: '#636e72' }}>Email</Typography>
                                  <Typography sx={{ fontWeight: 600 }}>{selectedItem.email}</Typography>
                                </Box>
                                <Box sx={{ flex: 2, p: 2, bgcolor: alpha(COLORS.info, 0.08), borderRadius: 2 }}>
                                  <Typography variant="caption" sx={{ color: '#636e72' }}>Última Actividad</Typography>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {selectedItem.last_activity ? new Date(selectedItem.last_activity).toLocaleString('es-ES') : 'N/A'}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: alpha(COLORS.gradient1, 0.08), borderRadius: 2, height: '100%' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Resumen de Cursos</Typography>
                              <Stack spacing={1}>
                                <Chip 
                                  icon={<CheckCircle />}
                                  label={`${selectedItem.completed_courses} Completados`} 
                                  sx={{ justifyContent: 'flex-start', bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.success } }}
                                />
                                <Chip 
                                  icon={<Schedule />}
                                  label={`${selectedItem.in_progress_courses} En Progreso`} 
                                  sx={{ justifyContent: 'flex-start', bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.primary } }}
                                />
                                <Chip 
                                  icon={<Pending />}
                                  label={`${selectedItem.not_started_courses} Inscritos`} 
                                  sx={{ justifyContent: 'flex-start', bgcolor: alpha('#636e72', 0.15), color: '#636e72', fontWeight: 600, '& .MuiChip-icon': { color: '#636e72' } }}
                                />
                              </Stack>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Cursos Completados */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: COLORS.success }} />
                        Cursos Completados
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedItem.courses?.filter(course => course.status === 'completed').length > 0 ? (
                          selectedItem.courses
                            .filter(course => course.status === 'completed')
                            .slice((completedCoursesPage - 1) * 6, completedCoursesPage * 6)
                            .map(course => (
                              <Grid item xs={12} sm={6} md={4} key={course.id}>
                                <CourseCard course={course} />
                              </Grid>
                            ))
                        ) : (
                          <Grid item xs={12}>
                            <Box sx={{ p: 4, textAlign: 'center', bgcolor: alpha(COLORS.light, 0.3), borderRadius: 2 }}>
                              <Typography sx={{ color: '#636e72' }}>No hay cursos completados</Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      {selectedItem.courses?.filter(course => course.status === 'completed').length > 6 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Pagination 
                            count={Math.ceil(selectedItem.courses.filter(course => course.status === 'completed').length / 6)} 
                            page={completedCoursesPage}
                            onChange={(event, value) => setCompletedCoursesPage(value)}
                            sx={{
                              '& .MuiPaginationItem-root.Mui-selected': {
                                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                                color: 'white'
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Grid>

                    {/* Cursos En Progreso */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: COLORS.primary }} />
                        Cursos En Progreso
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedItem.courses?.filter(course => course.status === 'in_progress').length > 0 ? (
                          selectedItem.courses
                            .filter(course => course.status === 'in_progress')
                            .map(course => (
                              <Grid item xs={12} sm={6} md={4} key={course.id}>
                                <Card sx={{ borderRadius: 3, border: '2px solid #e0e0e0', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${alpha(COLORS.primary, 0.15)}` } }}>
                                  <CardContent>
                                    <Box sx={{ 
                                      height: '60px', 
                                      mb: 2,
                                      background: `linear-gradient(135deg, ${alpha(COLORS.primary, 0.1)} 0%, ${alpha(COLORS.gradient2, 0.1)} 100%)`,
                                      p: 2,
                                      margin: -2,
                                      marginBottom: 2,
                                      borderRadius: '10px 10px 0 0',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {course.name}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                      <MenuBook sx={{ color: COLORS.primary, fontSize: 20 }} />
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {course.completed_lessons} de {course.total_lessons} lecciones
                                      </Typography>
                                    </Box>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={(course.completed_lessons / course.total_lessons) * 100}
                                      sx={{ 
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: alpha(COLORS.primary, 0.15),
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: COLORS.primary,
                                          borderRadius: 4
                                        }
                                      }}
                                    />
                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                      <Chip 
                                        icon={<Schedule />}
                                        label="En Progreso"
                                        size="small"
                                        sx={{ bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.primary } }}
                                      />
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))
                        ) : (
                          <Grid item xs={12}>
                            <Box sx={{ p: 4, textAlign: 'center', bgcolor: alpha(COLORS.light, 0.3), borderRadius: 2 }}>
                              <Typography sx={{ color: '#636e72' }}>No hay cursos en progreso</Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>

                    {/* Historial de Test */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assessment sx={{ color: COLORS.purple }} />
                        Historial de Tests
                      </Typography>
                      {selectedItem?.testHistory ? (
                        <Box sx={{ mt: 2 }}>
                          <TestHistory 
                            data={selectedItem.testHistory} 
                            isSupervisor={true}
                            userData={selectedItem}
                            hideStats={false}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: alpha(COLORS.light, 0.3), borderRadius: 2 }}>
                          <Typography sx={{ color: '#636e72' }}>No hay datos de test disponibles</Typography>
                        </Box>
                      )}
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* Info Curso */}
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid #e0e0e0' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Información del Curso</Typography>
                        <Typography variant="body2" sx={{ color: '#636e72', mb: 1 }}>
                          <strong>ID:</strong> {selectedItem.id}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#636e72' }}>
                          <strong>Descripción:</strong> {selectedItem.description || 'Sin descripción'}
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Análisis de Tests</Typography>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={testTabValue} onChange={(e, newValue) => setTestTabValue(newValue)}>
                          <Tab label="Análisis General" sx={{ textTransform: 'none', fontWeight: 600 }} />
                          <Tab label="Historial Detallado" sx={{ textTransform: 'none', fontWeight: 600 }} />
                        </Tabs>
                      </Box>
                      {testTabValue === 0 ? (
                        <CourseTestAnalytics data={selectedItem.testHistory || { attempts: [] }} />
                      ) : (
                        <TestHistory 
                          data={selectedItem.testHistory} 
                          isSupervisor={true}
                          userData={selectedItem}
                          showExportButtons={true}
                        />
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enroll Dialog */}
        <EnrollDialog
          open={openEnrollDialog}
          onClose={handleCloseEnrollDialog}
          selectedCourse={selectedCourse}
          selectedDepartment={selectedDepartment}
          selectedUser={selectedUser}
          deadlineDate={deadlineDate}
          isAnnual={isAnnual}
          departments={departments}
          usersInDepartment={usersInDepartment}
          onDepartmentChange={handleDepartmentChange}
          onUserChange={(e) => setSelectedUser(e.target.value)}
          onDateChange={(newValue) => setDeadlineDate(newValue)}
          onAnnualChange={(e) => setIsAnnual(e.target.checked)}
          onEnroll={handleEnrollUser}
        />
      </Container>
    </Box>
  );
};

export default SupervisorDashboard;